class StepFlow {
	constructor({ stage, templates, onRender, beforeRender, afterRender }) {
		this.stage = stage;
		this.templates = templates;
		this.onRender = onRender;
		this.beforeRender = beforeRender;
		this.afterRender = afterRender;

		this.currentStep = 1;

		this.hasRendered = false;

		this._bindPopState();
	}

	_bindPopState() {
		window.addEventListener("popstate", (e) => {
			const step = e.state?.step;

			if (!step) return;

			const direction = step > this.currentStep ? "forward" : "backward";

			this.currentStep = step;
			this.render(step, direction);
		});
	}

	isLoading() {
		return this.currentStep === "loading";
	}

	getNumericStep() {
		return this.isLoading()
			? this.templates.length + 1
			: this.currentStep;
	}

	getCurrentStep() {
		return this.currentStep;
	}

	getNextStep() {
		return (this.currentStep < this.templates.length)
			? this.currentStep + 1
			: this.currentStep;
	}

	getPreviousStep() {
		const step = this.getNumericStep();

		return step > 1 ? step - 1 : 1;
	}

	init() {
	  this.bindTabClicks();
	
	  // ✅ Read step from URL (if present)
	  const hash = window.location.hash;
	  const match = hash.match(/step-(\d+)/);
	
	  if (match) {
	    this.currentStep = Number(match[1]);
	  }
	
	  // ✅ DO NOT write to history here
	  this.render(this.currentStep);
	}

	goTo(step, { push = true } = {}) {
		if (step === this.currentStep) return;

		this.isNavigating = true;
		
		if (step === this.templates.length + 1) {
			step = "loading";
		}

		const direction = step > this.currentStep ? "forward" : "backward";
		this.currentStep = step;

		if (push) {
			if (step !== "loading") {
				history.pushState({ step }, "", `#step-${step}`);
			} else {
				history.replaceState('', document.title, window.location.href.split('#')[0]);
			}
		}

		this.render(step, direction);
	}

	next() {
		if (this.currentStep < this.templates.length) {
			this.goTo(this.currentStep + 1);
		} else {
			this.goTo("loading");
		}
	}

	prev() {
		const prevStep = this.getPreviousStep();
		this.goTo(prevStep);
	}

	syncTabs(stepState = this.currentStep) {
		const tabs = document.querySelectorAll(".step-tab");

		const isLoading = stepState === "loading";

		tabs.forEach((tab, index) => {
			const step = index + 1;

			tab.classList.remove("is-active", "is-complete");
			tab.setAttribute('disabled', true);

			if (isLoading) {
				tab.classList.add("is-complete");
				return;
			}

			if (step === this.currentStep) {
				tab.classList.add("is-active");
			}
			else if (step < this.currentStep) {
				tab.classList.add("is-complete");
				tab.removeAttribute('disabled');
			}
		});
	}

	bindTabClicks() {
		document.addEventListener("click", (e) => {
			const tab = e.target.closest(".step-tab");
			if (!tab) return;

			const step = Number(tab.dataset.step);
			if (!step) return;

			if (step < this.currentStep) {
				this.goTo(step);
			}
		});
	}

	async render(step, direction) {
		const id = (step === "loading") ? "step-loading" : `step-${step}`;
		const tpl = document.getElementById(id);

		if (this.beforeRender) {
    		await this.beforeRender(step, direction);
		}
		
		const updateDOM = () => {
			this.stage.dataset.direction = direction || "forward";
			this.stage.replaceChildren(tpl.content.cloneNode(true));
			this.onRender(this.stage, step, direction);
			this.syncTabs(step);
		};

		if (!this.hasRendered || !document.startViewTransition) {
		    updateDOM();
		    this.hasRendered = true;
		
			if (this.afterRender) {
			    this.afterRender(step, direction, {
			      isNavigating: this.isNavigating === true
			    });
			}
			this.isNavigating = false;
		    return;
		}
	
	  	const transition = document.startViewTransition(() => {
	    	updateDOM();
		});
	
		await transition.finished;
	
		if (this.afterRender) {
		   this.afterRender(step, direction, {
		    isNavigating: this.isNavigating === true
		  });
		}
		
		this.isNavigating = false;
	}
}

export { StepFlow };
